import {
  ChartType,
  type BaseDataPoint,
  type ChartClickEventArgs,
  type ChartDataset,
} from '@powerportalspro/react-charts';
import { FluentUIChart } from '@powerportalspro/react-fluent';
import { useT } from '@powerportalspro/react';
import { useMemo, useState } from 'react';

// Reuses the Blazor demo's localization keys (BarChartMainDemo) so both stacks share strings.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Charts.BarChart.BarChartMainDemo';

// Sales DataPoint variant that carries Region / OrderCount alongside the
// numeric value. Mirrors Blazor's `SalesDataPoint : DataPoint` pattern —
// extend `BaseDataPoint` for the value/color fields and add whatever
// extra context the click handler should be able to recover.
//
// The dataset / chart get parameterized with this type below
// (`ChartDataset<SalesDataPoint>`, `<FluentUIChart<SalesDataPoint> …>`),
// so the click handler's `args.dataPoint` is typed automatically —
// no `as unknown as …` casts required at either the construction or
// recovery side.
interface SalesDataPoint extends BaseDataPoint {
  region: string;
  orderCount: number;
}

export function BarChartMainSample() {
  const t = useT();
  const [lastClick, setLastClick] = useState<ChartClickEventArgs<SalesDataPoint> | null>(null);

  // Labels + datasets are rebuilt when the locale changes (t changes identity) so the
  // axis labels and legend follow the selected culture.
  const labels = useMemo(
    () => [
      t(`${STRINGS_BASE}.month-jan`),
      t(`${STRINGS_BASE}.month-feb`),
      t(`${STRINGS_BASE}.month-mar`),
      t(`${STRINGS_BASE}.month-apr`),
      t(`${STRINGS_BASE}.month-may`),
      t(`${STRINGS_BASE}.month-jun`),
      t(`${STRINGS_BASE}.month-jul`),
    ],
    [t],
  );

  // The Sales dataset uses the SalesDataPoint variant; the other two
  // datasets use the default `BaseDataPoint` since they have no extra
  // context to surface. They coexist in the same `ChartDataset<SalesDataPoint>[]`
  // array because every BaseDataPoint shape is structurally compatible
  // with a richer SalesDataPoint when its extra fields are absent.
  const datasets = useMemo<ChartDataset<SalesDataPoint>[]>(() => {
    const north = t(`${STRINGS_BASE}.region-north`);
    const south = t(`${STRINGS_BASE}.region-south`);
    const east = t(`${STRINGS_BASE}.region-east`);
    const west = t(`${STRINGS_BASE}.region-west`);
    return [
      {
        label: t(`${STRINGS_BASE}.series-sales`),
        data: [
          { value: 65, region: north, orderCount: 12 },
          { value: 59, region: north, orderCount: 10 },
          { value: 80, region: south, orderCount: 18 },
          { value: 81, region: south, orderCount: 19 },
          { value: 56, region: east, orderCount: 9 },
          { value: 55, region: east, orderCount: 9 },
          { value: 40, region: west, orderCount: 7 },
        ],
      },
      {
        label: t(`${STRINGS_BASE}.series-returns`),
        data: [12, 19, 8, 15, 6, 9, 4].map((v) => ({ value: v, region: '', orderCount: 0 })),
      },
      {
        label: t(`${STRINGS_BASE}.series-refunds`),
        data: [3, 5, 2, 6, 1, 4, 2].map((v) => ({ value: v, region: '', orderCount: 0 })),
      },
    ];
  }, [t]);

  // No cast — `lastClick.dataPoint` is typed `SalesDataPoint | undefined`
  // because we parameterized the FluentUIChart's generic below.
  const sales = lastClick?.dataPoint;
  return (
    <>
      <FluentUIChart<SalesDataPoint>
        type={ChartType.Bar}
        labels={labels}
        datasets={datasets}
        onElementClick={setLastClick}
      />
      {lastClick && (
        <p style={{ marginTop: '12px' }}>
          <strong>{t(`${STRINGS_BASE}.clicked-label`)}:</strong> {lastClick.datasetLabel} —{' '}
          {lastClick.label} = {lastClick.value}
          {sales?.region && (
            <span>
              {' '}&nbsp;
              <em>
                ({t(`${STRINGS_BASE}.region-prefix`)} {sales.region}, {t(`${STRINGS_BASE}.orders-prefix`)}{' '}
                {sales.orderCount})
              </em>
            </span>
          )}
        </p>
      )}
    </>
  );
}
